import "../types/index.js";

import videojs from "video.js";
// Plugins must load after video.js
import "@videojs/http-streaming";
import "videojs-contrib-eme";

import { AnimationDuration } from "../constants/animation_duration.js";
import { AnalyticsEvent } from "../events/analytics_event.js";
import { createCustomEvent } from "../events/custom_event.js";
import { FeedEvent } from "../events/feed_event.js";
import { TapEvent } from "../events/tap_event.js";
import { TapGesture } from "../mixins/tap_gesture.js";
import { UseMixins } from "../mixins/use_mixins.js";
import { decode } from "../utilities/base58.js";
import { Deferred } from "../utilities/deferred.js";
import { createElement, createFragment } from "../utilities/elements.js";
import { supportsTouch } from "../utilities/media.js";
import { isShareSupported } from "../utilities/sharing.js";
import { DickTokComponent } from "./Component.js";
import { defineElement } from "./registry.js";

/**
 * @typedef {import("../types/types.js").UsingMixin<DickTokComponent, TapGestureInterface>} UsingTapGesture
 * @typedef {import("../types/tap_gesture.js").TapGestureInterface} TapGestureInterface
 */

const PLAY_ABORTED_PAUSE =
  "AbortError: The play() request was interrupted by a call to pause().";
const PLAY_ABORTED_BACKGROUND =
  "AbortError: The play() request was interrupted because video-only " +
  "background media was paused to save power.";

export class DickTokPost extends UseMixins(DickTokComponent, [TapGesture]) {
  static services = [
    "AnalyticsService",
    "LoggingService",
    "PostApiService",
    "SheetService",
    "VolumeService",
  ];

  static subscriptions = ["FeedEvents"];

  static attributes = [
    { name: "image-src", type: String },
    { name: "liked", type: Boolean },
    /** base58 encoded post id */
    { name: "post-id", type: String },
    { name: "poster-id", type: Number },
    { name: "poster-username", type: String },
    { name: "saved", type: Boolean },
    { name: "videos", type: Array },
  ];

  static targets = [
    "description",
    "likeButton",
    "likesCount",
    "muteButton",
    "overflowMenuButton",
    "poster",
    "saveButton",
  ];

  /** @type {Deferred<any>} videojs Player instance */
  #videoPlayerLoad = new Deferred();

  connectedCallback() {
    super.connectedCallback();
    this.loggingService.log("Post#connectedCallback");
    this.#setupListeners();
    this.#initializeVideo();
  }

  async onViewportFocus() {
    // Wait for the player to load, but we don't need the instance here
    await this.#videoPlayer;
    this.volumeService.muted
      ? this.#mute(/* showOverlay= */ false)
      : this.#unmute(/* showOverlay= */ false);
    this.#play();
    this.analyticsService.event(AnalyticsEvent.POST_VIEW, {
      post_id: decode(this.postId),
      poster_id: this.posterId,
    });
  }

  async onViewportBlur() {
    // Cleanup any waiting taps that haven't fired
    this.cancelTap();
    // Pause the video
    this.#pause();
  }

  async onNearViewport() {
    const player = await this.#videoPlayer;
    player.preload("auto");
    player.load();
  }

  /** @param {MouseEvent|TouchEvent} event */
  sharePost = async (event) => {
    // See comment in #setupListeners() for why we call stopPropagation().
    //
    // stopPropagation() can be called in an async EventListener, but it needs
    // to be called in the listener itself (meaning, not in a chained promise,
    // in other words, before any `await` keywords), so do this first.
    event.stopPropagation();

    const shareText = `Check out this post by @${this.posterUsername} on DickTok`;
    const shareUrl = `${location.origin}/posts/${this.postId}`;

    // This is dynamically decided by the browser using multiple factors. In
    // particular, it only works in response to user input.
    if (navigator.canShare) {
      // Open share dialog
      await navigator.share({
        title: document.title,
        text: shareText,
        url: shareUrl,
      });
    } else {
      // We could improve this by showing a dialog or snackbar, then hiding the
      // share option by using a data attribute and CSS-rule-based styling.
      this.error("Share failed");
    }

    // Close overflow menu
    this.sheetService.close();
  };

  /** @param {MouseEvent|TouchEvent} event */
  reportPost = async (event) => {
    // See comment in #setupListeners() for why we call stopPropagation().
    //
    // stopPropagation() can be called in an async EventListener, but it needs
    // to be called in the listener itself (meaning, not in a chained promise,
    // in other words, before any `await` keywords), so do this first.
    event.stopPropagation();

    open(
      `https://justfor.fans/report.php?PosterName=${this.posterUsername}&PostID=${decode(this.postId)}`,
      "_blank",
      "noopener",
    );

    // Close overflow menu
    this.sheetService.close();
  };

  #setupListeners() {
    // Automatically remove event listeners on disconnect
    const options = { signal: this.disconnectSignal };

    // Feed events
    this.feedEvents.addEventListener(
      FeedEvent.FEED_PAN_START,
      ({ detail }) => {
        this.dataset.panDirection = detail.axis;
      },
      options,
    );
    this.feedEvents.addEventListener(
      FeedEvent.FEED_PAN_END,
      () => {
        delete this.dataset.panDirection;
      },
      options,
    );

    // Tap events
    this.addEventListener(TapEvent.TAP, this.#handleTap, options);
    this.addEventListener(TapEvent.DOUBLE_TAP, this.#likePost, options);

    const addEventListenerWithStopPropagation =
      /**
       * TapGesture listens to events to fire a TapEvent, so we call
       * `stopPropagation()` to prevent the event from bubbling. Otherwise,
       * `#handleTap()` would be called and cause unexpected behavior.
       *
       * TapGesture adds listeners to "touchend" or "click" and the event must
       * match exactly for `stopPropagation()` to work. This is also why we
       * can't use data-action attributes to add these listeners.
       * @param {HTMLElement} element
       * @param {Function} handler
       */
      (element, handler) => {
        const eventName = supportsTouch() ? "touchend" : "click";
        element.addEventListener(
          eventName,
          /** @param {Event} event */ (event) => {
            event.stopPropagation();
            handler(event);
          },
          options,
        );
      };

    // Button events
    if (this.hasDescriptionTarget) {
      addEventListenerWithStopPropagation(this.descriptionTarget, () => {
        this.dataset.expanded ? this.#collapse() : this.#expand();
      });
    }
    addEventListenerWithStopPropagation(this.muteButtonTarget, () => {
      this.dataset.muted ? this.#unmute() : this.#mute();
    });
    addEventListenerWithStopPropagation(this.likeButtonTarget, () => {
      this.liked ? this.#unlikePost() : this.#likePost();
    });
    addEventListenerWithStopPropagation(this.saveButtonTarget, () => {
      this.saved ? this.#unsavePost() : this.#savePost();
    });
    addEventListenerWithStopPropagation(this.overflowMenuButtonTarget, () => {
      this.#openOverflowMenu();
    });
  }

  async #initializeVideo() {
    // Don't try and initialize again if already loaded
    if (!this.#videoPlayerLoad.pending) {
      return;
    }

    // `videojs()` creates the Player and calls the `ready` callback when the
    // Player and all tech are ready, we wrap this in a promise to await it.
    /** @type {VideoJsPlayer} */
    const player = await new Promise((resolve) => {
      /** @type {HTMLElement} */
      const videoJsElement = this.getElement("video-js");
      const videoJsOptions = {
        /* Native video element options */
        autoplay: false, // plays in `onViewportFocus()`
        loop: true,
        poster: this.imageSrc,
        preload: "none", // loaded in `onNearViewport()`
        muted: true, // must be muted for auto/scripted play
        /* Safari options */
        playsinline: true, // prevent default fullscreen
        /* video.js options */
        disablePictureInPicture: true,
        inactivityTimeout: 0, // keep controls visible
        userActions: false, // player managed using API
        bigPlayButton: false,
        controlBar: {
          // only show the progress bar
          children: ["progressControl"],
        },
        titleBar: false,
        textTrackDisplay: false,
        /* Streaming options */
        html5: { vhs: { handleManifestRedirects: true } },
      };
      // `videojs()` actually does return the Player, but it might not be fully
      // loaded along with all techs and plugins, so we wait for the `ready`
      // callback.
      videojs(videoJsElement, videoJsOptions, () =>
        // The `ready` callback indicates the Player is ready, but doesn't
        // return a reference, so then call `getPlayer()`.
        resolve(videojs.getPlayer(videoJsElement)),
      );
    });

    // Initialize EME, must be before setting the source
    player.eme();

    // Set the video source
    if (!player.paused()) {
      this.#pause(player);
      player.src(this.videos);
      this.#play(player);
    } else {
      player.src(this.videos);
    }

    // Prevent right click/long press from downloading poster image
    player.on(
      "contextmenu",
      /** @param {Event} event */ (event) => event.preventDefault(),
    );

    // Dispose the player when the component is disconnected
    this.disconnectSignal.addEventListener("abort", () => {
      player.dispose();
    });

    this.#videoPlayerLoad.resolve(player);
  }

  /** @returns {Promise<videojs>} */
  get #videoPlayer() {
    return new Promise(async (resolve, reject) => {
      const player = await this.#videoPlayerLoad;
      player.isDisposed() ? reject("Player is disposed") : resolve(player);
    });
  }

  /**
   * Check videojs Player.play() to avoid throwing errors.
   * @param {?Player} player
   */
  async #play(player = null) {
    player = player ?? (await this.#videoPlayer);
    if (!player.paused()) {
      return;
    }
    try {
      player.play();
    } catch (error) {
      if (
        error instanceof DOMException &&
        ![PLAY_ABORTED_PAUSE, PLAY_ABORTED_BACKGROUND].includes(error.message)
      ) {
        this.error(error, this);
      }
    }
  }

  /**
   * Check videojs Player.pause() to avoid throwing errors.
   * @param {?Player} player
   */
  async #pause(player = null) {
    player = player ?? (await this.#videoPlayer);
    if (player.paused()) {
      return;
    }
    player.pause();
  }

  /**
   * If the post description is expanded, collapse it. Otherwise toggle the
   * video between playing and paused.
   */
  #handleTap = async () => {
    if (this.dataset.expanded) {
      this.#collapse();
      return;
    }
    const player = await this.#videoPlayer;
    if (player.paused()) {
      this.#play();
    } else {
      await this.#pause();
      this.#animateControlsOverlay("fa-pause");
    }
  };

  /** Expand the post description */
  #expand() {
    // Dispatch an event that will be caught by the DickTokFeed to disable
    // swiping while the description is open
    this.feedEvents.dispatchEvent(
      createCustomEvent(FeedEvent.DISABLE_FEED_SCROLLING),
    );
    // Pause the video when the description opens
    this.#pause();
    // Add dimming to video
    const videoElement = this.getElement("video");
    const dimAnimation = videoElement.animate(
      [{ filter: "brightness(1)" }, { filter: "brightness(0.5)" }],
      AnimationDuration.SHORT,
    );
    dimAnimation.finished.then(() => {
      videoElement.style.filter = "brightness(0.5)";
    });
    this.descriptionTarget.open().then(() => {
      this.dataset.expanded = "expanded";
    });
  }

  /** Collapse the post description */
  #collapse() {
    // Remove dimming from video
    const videoElement = this.getElement("video");
    const dimAnimation = videoElement.animate(
      [{ filter: "brightness(0.5)" }, { filter: "brightness(1)" }],
      AnimationDuration.SHORT,
    );
    dimAnimation.finished.then(() => {
      videoElement.style.filter = "";
    });
    this.descriptionTarget.close().then(() => {
      // Play the video when the description closes
      this.#play();
      // Dispatch an event that will be caught by the DickTokFeed to enable
      // swiping now the description is closed
      this.feedEvents.dispatchEvent(
        createCustomEvent(FeedEvent.ENABLE_FEED_SCROLLING),
      );
      delete this.dataset.expanded;
    });
  }

  async #mute(showOverlay = true) {
    const player = await this.#videoPlayer;
    player.muted(true);
    this.volumeService.mute();
    if (showOverlay) {
      this.#animateControlsOverlay("fa-volume-slash");
    }
    this.muteButtonTarget.firstElementChild.classList.replace(
      "fa-volume-slash",
      "fa-volume",
    );
    this.dataset.muted = "muted";
  }

  async #unmute(showOverlay = true) {
    const player = await this.#videoPlayer;
    player.muted(false);
    this.volumeService.unmute();
    if (showOverlay) {
      this.#animateControlsOverlay("fa-volume");
    }
    this.muteButtonTarget.firstElementChild.classList.replace(
      "fa-volume",
      "fa-volume-slash",
    );
    delete this.dataset.muted;
  }

  #likePost = async () => {
    // We allow liking multiple times from double tap so check
    if (!this.liked) {
      // just assume it works
      this.postApiService.likePost(this.postId, this.posterId);
      // update the count
      const likesCount = Number(this.likesCountTarget.innerText);
      // Will be false if value was NaN like "1.1K"
      if (Number.isFinite(likesCount)) {
        this.likesCountTarget.innerText = likesCount + 1;
      }
    }
    // create, animate and remove hero like
    this.#animateLikeOverlay();
    // animate the button
    await this.#animateButtonIn(this.likeButtonTarget);
    // This toggles the color so do it when the animation finishes
    this.liked = true;
  };

  async #unlikePost() {
    // just assume it works
    this.postApiService.unlikePost(this.postId, this.posterId);
    // update the count
    const likesCount = Number(this.likesCountTarget.innerText);
    // Will be false if value was unparseable like "1.1K"
    if (Number.isFinite(likesCount)) {
      this.likesCountTarget.innerText = likesCount - 1;
    }
    // animate the button
    await this.#animateButtonOut(this.likeButtonTarget);
    // This toggles the color so do it when the animation finishes
    this.liked = false;
  }

  async #savePost() {
    // just assume it works
    this.postApiService.savePost(this.postId, this.posterId);
    // animate the button
    this.#animateButtonIn(this.saveButtonTarget);
    // This toggles the color so do it when the animation finishes
    this.saved = true;
  }

  async #unsavePost() {
    // just assume it works
    this.postApiService.unsavePost(this.postId, this.posterId);
    // animate the button
    await this.#animateButtonOut(this.saveButtonTarget);
    // This toggles the color so do it when the animation finishes
    this.saved = false;
  }

  async #openOverflowMenu() {
    // See comment in #setupListeners() on event type
    const eventType = supportsTouch() ? "touchend" : "click";
    // Only show share menu button if browser supports the API
    const shareMenuItem = isShareSupported()
      ? `<dt-menuitem
              data-action="${eventType}:dicktok-post->sharePost"
              icon-name="share"
          >
              Share
          </dt-menuitem>
          <dt-menudivider></dt-menudivider>`
      : "";
    const sheet = createFragment(`
      <dt-menu>
          ${shareMenuItem}
          <dt-menuitem
              data-action="${eventType}:dicktok-post->reportPost"
              icon-name="exclamation-triangle"
          >
              Report
          </dt-menuitem>
      </dt-menu>`);
    // Disable feed swiping when the sheet opens
    this.feedEvents.dispatchEvent(
      createCustomEvent(FeedEvent.DISABLE_FEED_SCROLLING),
    );
    // Pause the video when the sheet opens
    this.#pause();
    // Open the sheet
    const onClose = this.sheetService.open(this, sheet);
    onClose.then(() => {
      // We don't automatically play the video again since either the share
      // dialog is open (and there's no way to tell when it closes) or another
      // tab has been opened with the report post form.
      //
      // Enable feed swiping when the sheet closes.
      this.feedEvents.dispatchEvent(
        createCustomEvent(FeedEvent.ENABLE_FEED_SCROLLING),
      );
    });
  }

  async #animateLikeOverlay() {
    const overlay = createElement(`
      <div class="dicktok-post__overlay">
        <span class="dicktok-post__overlay-like-icon fa-sharp fa-heart">
        </span>
      </div>
    `);
    this.appendChild(overlay);
    const animation = overlay.animate(
      [
        { transform: "scale(0)" },
        { transform: "scale(1.4)", offset: 0.666 },
        { transform: "scale(1)" },
      ],
      AnimationDuration.LONG,
    );
    await animation.finished;
    await new Promise((resolve) =>
      window.setTimeout(() => resolve(), AnimationDuration.LONG),
    );
    this.removeChild(overlay);
  }

  /** @param {string} icon Icon to show in overlay */
  async #animateControlsOverlay(icon) {
    const overlay = createElement(`
      <div class="dicktok-post__overlay">
        <div class="dicktok-post__overlay-pause-background">
          <span class="dicktok-post__overlay-pause-icon fa-sharp ${icon}">
          </span>
        </div>
      </div>
    `);
    this.appendChild(overlay);
    await new Promise((resolve) =>
      window.setTimeout(() => resolve(), AnimationDuration.LONG),
    );
    const animation = overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
      easing: "ease-out",
      duration: AnimationDuration.LONG,
    });
    await animation.finished;
    this.removeChild(overlay);
  }

  async #animateButtonIn(element) {
    const animation = element.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.4)", offset: 0.666 },
        { transform: "scale(1)" },
      ],
      AnimationDuration.SHORT,
    );
    await animation.finished;
  }

  async #animateButtonOut(element) {
    const animation = element.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(0.6)", offset: 0.666 },
        { transform: "scale(1)" },
      ],
      AnimationDuration.SHORT,
    );
    await animation.finished;
  }
}

defineElement("dicktok-post", DickTokPost);
